import {
  purchaseHistorySchema,
  purchaseOrderSchema,
  reorderSuggestionsSchema,
  supplierPaymentSchema,
  supplierSchema,
  supplierStatementSchema,
  type CreatePurchaseOrderInput,
  type CreateSupplierInput,
  type PaySupplierInput,
  type PurchaseHistory,
  type PurchaseOrder,
  type ReceiveGoodsInput,
  type ReorderSuggestions,
  type Supplier,
  type SupplierPayment,
  type SupplierStatement,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every purchasing call the app makes.
 *
 * There is no "set stock quantity" here, for the same reason there is none in the stock client:
 * the API has no such route. A delivery is recorded as what ARRIVED, and the shelf follows from
 * the movements the server writes.
 */

const BASE = '/purchasing';

export async function getSuppliers(client: ApiClient): Promise<Supplier[]> {
  const body = await client.get(`${BASE}/suppliers`, {
    schema: z.object({ items: z.array(supplierSchema) }),
  });
  return body.items;
}

export async function createSupplier(
  client: ApiClient,
  input: CreateSupplierInput,
): Promise<Supplier> {
  return await client.post(`${BASE}/suppliers`, input, { schema: supplierSchema });
}

export async function getSupplierStatement(
  client: ApiClient,
  supplierId: string,
): Promise<SupplierStatement> {
  return await client.get(`${BASE}/suppliers/${supplierId}/statement`, {
    schema: supplierStatementSchema,
  });
}

export async function paySupplier(
  client: ApiClient,
  supplierId: string,
  input: PaySupplierInput,
): Promise<SupplierPayment> {
  return await client.post(`${BASE}/suppliers/${supplierId}/pay`, input, {
    schema: supplierPaymentSchema,
  });
}

export async function getPurchaseOrders(
  client: ApiClient,
  options: { openOnly?: boolean } = {},
): Promise<PurchaseOrder[]> {
  const body = await client.get(`${BASE}/orders`, {
    schema: z.object({ items: z.array(purchaseOrderSchema) }),
    ...(options.openOnly ? { query: { open: 'true' } } : {}),
  });
  return body.items;
}

export async function createPurchaseOrder(
  client: ApiClient,
  input: CreatePurchaseOrderInput,
): Promise<PurchaseOrder> {
  return await client.post(`${BASE}/orders`, input, { schema: purchaseOrderSchema });
}

export async function sendPurchaseOrder(
  client: ApiClient,
  purchaseOrderId: string,
): Promise<PurchaseOrder> {
  return await client.post(
    `${BASE}/orders/${purchaseOrderId}/send`,
    {},
    {
      schema: purchaseOrderSchema,
    },
  );
}

/** The order as text the shop pastes into WhatsApp — which is how these are actually placed. */
export async function getPurchaseOrderMessage(
  client: ApiClient,
  purchaseOrderId: string,
): Promise<string> {
  const body = await client.get(`${BASE}/orders/${purchaseOrderId}/message`, {
    schema: z.object({ message: z.string() }),
  });
  return body.message;
}

export async function receiveGoods(
  client: ApiClient,
  purchaseOrderId: string,
  input: ReceiveGoodsInput,
): Promise<{ order: PurchaseOrder }> {
  return await client.post(`${BASE}/orders/${purchaseOrderId}/receive`, input, {
    schema: z.object({ order: purchaseOrderSchema }),
  });
}

export async function getReorderSuggestions(client: ApiClient): Promise<ReorderSuggestions> {
  return await client.get(`${BASE}/orders/reorder-suggestions`, {
    schema: reorderSuggestionsSchema,
  });
}

export async function getPurchaseHistory(
  client: ApiClient,
  stockItemId: string,
): Promise<PurchaseHistory> {
  return await client.get(`${BASE}/history/${stockItemId}`, { schema: purchaseHistorySchema });
}
